'use client'
import * as React from 'react'
import { Copy, Check, Package, Code2, Terminal } from 'lucide-react'

/**
 * Three-step Quick Start card for the @idukki/widget-sdk npm package.
 * - Install: `npm i @idukki/widget-sdk`
 * - Mount: `IddukkiWidget.load(GALLERY_GUID, { bguid, container })`
 * - Framework snippets: React (default), Vue, Angular, vanilla
 *
 * Copy buttons fire a clipboard write + flip to "Copied" for 1.5s.
 * Tabs swap the visible snippet. Vue / Angular / vanilla are common
 * customer asks and the SDK is framework-agnostic.
 */

const SNIPPETS = {
  install: `npm i @idukki/widget-sdk`,
  basic: `import { IddukkiWidget } from '@idukki/widget-sdk';

IddukkiWidget.load('YOUR_GALLERY_GUID');`,
  options: `IddukkiWidget.load('YOUR_GALLERY_GUID', {
  bguid: 'YOUR_BUSINESS_GUID',
  container: document.getElementById('my-widget-slot'),
});`,
  react: `import { useEffect } from 'react';
import { IddukkiWidget } from '@idukki/widget-sdk';

export default function GallerySection() {
  useEffect(() => {
    IddukkiWidget.load('YOUR_GALLERY_GUID');
  }, []);

  return <div id="my-widget-slot" />;
}`,
  vue: `<script setup>
import { onMounted } from 'vue';
import { IddukkiWidget } from '@idukki/widget-sdk';

onMounted(() => {
  IddukkiWidget.load('YOUR_GALLERY_GUID');
});
</script>

<template>
  <div id="my-widget-slot" />
</template>`,
  angular: `import { Component, OnInit } from '@angular/core';
import { IddukkiWidget } from '@idukki/widget-sdk';

@Component({
  selector: 'app-gallery',
  template: '<div id="my-widget-slot"></div>',
})
export class GalleryComponent implements OnInit {
  ngOnInit() {
    IddukkiWidget.load('YOUR_GALLERY_GUID');
  }
}`,
  vanilla: `<div id="my-widget-slot"></div>
<script type="module">
  import { IddukkiWidget } from 'https://esm.sh/@idukki/widget-sdk';
  IddukkiWidget.load('YOUR_GALLERY_GUID');
</script>`,
}

type Framework = 'react' | 'vue' | 'angular' | 'vanilla'

const FRAMEWORKS: { key: Framework; label: string }[] = [
  { key: 'react',   label: 'React / Next' },
  { key: 'vue',     label: 'Vue / Nuxt' },
  { key: 'angular', label: 'Angular' },
  { key: 'vanilla', label: 'Vanilla / CDN' },
]

export function NpmInstallCard() {
  const [framework, setFramework] = React.useState<Framework>('react')
  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Package className="h-4 w-4" />
          </span>
          <div>
            <p className="font-display text-base font-semibold leading-tight">@idukki/widget-sdk</p>
            <p className="text-[11px] text-muted-foreground">npm · ESM + CJS · framework-agnostic</p>
          </div>
        </div>
        <span className="rounded-full border bg-secondary px-2.5 py-0.5 font-mono text-[11px] text-foreground">v1.0.0</span>
      </div>

      <div className="space-y-5 p-6">
        <Step n="1" icon={Terminal} title="Install">
          <CodeBlock code={SNIPPETS.install} language="bash" />
        </Step>

        <Step n="2" icon={Code2} title="Mount">
          <p className="mb-3 text-sm text-muted-foreground">Minimal, pass your gallery GUID:</p>
          <CodeBlock code={SNIPPETS.basic} language="js" />
          <p className="mt-4 mb-2 text-sm text-muted-foreground">With business GUID + a specific container:</p>
          <CodeBlock code={SNIPPETS.options} language="js" />
        </Step>

        <Step n="3" icon={Code2} title="Drop in any framework">
          <div className="mb-3 flex flex-wrap gap-1.5">
            {FRAMEWORKS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFramework(f.key)}
                className={`rounded-full border px-3 py-1 text-xs transition ${framework === f.key ? 'border-primary bg-primary/10 text-primary' : 'border-input bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <CodeBlock code={SNIPPETS[framework]} language={framework === 'vue' ? 'html' : framework === 'vanilla' ? 'html' : 'js'} />
        </Step>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-secondary/30 px-6 py-3 text-xs text-muted-foreground">
        <span>Your gallery GUID lives in the Idukki dashboard → <span className="font-medium text-foreground">Widget Settings → Embed Code</span>.</span>
        <a href="https://www.npmjs.com/package/@idukki/widget-sdk" target="_blank" rel="noopener noreferrer" className="font-medium text-primary hover:underline">View on npm →</a>
      </div>
    </div>
  )
}

function Step({ n, icon: Icon, title, children }: { n: string; icon: typeof Code2; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
        <span className="flex h-5 w-5 items-center justify-center rounded-full border bg-card text-foreground">{n}</span>
        <Icon className="h-3 w-3" />
        {title}
      </div>
      {children}
    </div>
  )
}

function CodeBlock({ code, language }: { code: string; language: string }) {
  const [copied, setCopied] = React.useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked, silently ignore */ }
  }
  return (
    <div className="group relative overflow-hidden rounded-lg border bg-slate-950 text-slate-100">
      <div className="flex items-center justify-between border-b border-slate-800 px-3 py-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{language}</span>
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] font-medium text-slate-300 transition hover:bg-slate-800 hover:text-white"
        >
          {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
        </button>
      </div>
      <pre className="overflow-x-auto px-4 py-3 font-mono text-[12px] leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  )
}
